import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store';
import { client } from '../services/api';

type MutateOneFn = (billingCycleId: string) => Promise<unknown>;
type OnAfterReloadFn = () => Promise<void> | void;

type MutateSelectedFailure = {
  id: string;
  name: string;
  error: unknown;
};

interface UseBillingCyclesProps<T, P> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchFn: (args: any) => Promise<{ data?: T[] }>;
  fetchPayload: P;
  errorMessage: string;
  getId: (item: T) => string;
  getName: (item: T) => string;
}

export function useBillingCycles<T, P>({
  fetchFn,
  fetchPayload,
  errorMessage,
  getId,
  getName,
}: UseBillingCyclesProps<T, P>) {
  const showError = useStore(state => state.showError);
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isApiErrorResponse = (value: unknown): value is { error: unknown } => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'error' in value &&
      (value as { error?: unknown }).error !== undefined
    );
  };

  // Stringify payload to use as a stable dependency for useCallback/useEffect
  const payloadString = JSON.stringify(fetchPayload);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchFn({
        client,
        body: JSON.parse(payloadString),
      });
      setData(response.data ?? []);
      // Clear selection when data reloads
      setSelectedIds(new Set());
    } catch {
      setData([]);
      showError('Failed to load billing cycles', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn, payloadString, errorMessage, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleId = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedIds(prev =>
      prev.size === data.length ? new Set() : new Set(data.map(getId))
    );
  }, [data, getId]);

  const mutateSelected = useCallback(
    async (
      mutateOne: MutateOneFn,
      failureMessageTitle: string,
      failureMessagePrefix: string,
      onAfterReload?: OnAfterReloadFn,
      onFailure?: (failures: MutateSelectedFailure[]) => void
    ) => {
      setIsSubmitting(true);
      const ids = Array.from(selectedIds);
      try {
        const results = await Promise.allSettled(ids.map(id => mutateOne(id)));
        const failures: MutateSelectedFailure[] = [];

        for (let i = 0; i < ids.length; i += 1) {
          const id = ids[i];
          const item = data.find(e => getId(e) === id);
          const name = item ? getName(item) : id;
          const result = results[i];

          if (result.status === 'rejected') {
            failures.push({ id, name, error: result.reason });
            continue;
          }

          if (isApiErrorResponse(result.value)) {
            // Preserve the full AxiosError so callers can read status + detail.
            failures.push({ id, name, error: result.value });
          }
        }

        if (failures.length > 0) {
          if (onFailure) {
            onFailure(failures);
          } else {
            const failedNames = failures.map(f => f.name).join(', ');
            showError(
              failureMessageTitle,
              `${failureMessagePrefix}: ${failedNames}`
            );
          }
        }
        const hasSuccessfulMutations = failures.length < ids.length;

        setSelectedIds(new Set());
        await loadData();
        if (hasSuccessfulMutations) {
          await onAfterReload?.();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedIds, data, loadData, showError, getId, getName]
  );

  return {
    data,
    isLoading,
    isSubmitting,
    selectedIds,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    loadData,
  };
}
